import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import { ProductOption } from '../../../../interfaces/ProductOption/ProductOption';
import { sharedColors, sharedStyles } from '../../../../utils/Style';
import NumberInput from '../../../../components/NumberInput/NumberInput';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { ProductOptionKey } from '../../../../interfaces/ProductOption/ProductOptionKey';
import { UpsertProductOptionDialog } from './UpsertProductOptionDialog';

interface ProductOptionCardProps {
  value: ProductOption;
  keys: ProductOptionKey[];
  onChange: (newValue: ProductOption) => void;
  onDelete: () => void;
  onUpdateValues: (newValues: string[]) => void;
}

export const ProductOptionCard = (props: ProductOptionCardProps) => {
  const [clickedDelete, setClickedDelete] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);

  const handlePriceChange = (index: number, newPrice: number) => {
    const newPrices = [...props.value.pricing.prices];
    newPrices[index] = newPrice;

    props.onChange({
      ...props.value,
      basePrice: index ? props.value.basePrice : newPrice,
      pricing: { ...props.value.pricing, prices: newPrices },
    });
  };

  const handleUpdateValues = (newValues: string[]) => {
    props.onUpdateValues(newValues);
    setShowUpdateDialog(false);
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        width: 450,
        p: 3,
        height: 'fit-content',
      }}
    >
      <UpsertProductOptionDialog
        keys={props.keys}
        initialValues={props.value.kvps.map((kvp) => kvp.value)}
        open={showUpdateDialog}
        onUpsert={handleUpdateValues}
        onClose={() => setShowUpdateDialog(false)}
      />
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili varyantı silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={() => {
            setClickedDelete(false);
            props.onDelete();
          }}
          color='error'
        />
      )}

      <Typography
        onClick={() => setShowUpdateDialog(true)}
        sx={{
          ...sharedStyles.body2,
          color: sharedColors.gray4,
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline', // Underline on hover
          },
        }}
      >
        {props.value.kvps.map((kvp) => kvp.value).join(' / ')}
      </Typography>

      <TextField
        label='Barkod'
        variant='outlined'
        size='small'
        multiline
        value={props.value.barcode ?? ''}
        onChange={(e) =>
          props.onChange({ ...props.value, barcode: e.target.value })
        }
        fullWidth
      />

      <TextField
        label='SKU'
        variant='outlined'
        size='small'
        multiline
        value={props.value.sku ?? ''}
        onChange={(e) =>
          props.onChange({ ...props.value, sku: e.target.value })
        }
        fullWidth
      />

      <TextField
        label='HB SKU'
        variant='outlined'
        size='small'
        multiline
        value={props.value.hepsiburada_sku ?? ''}
        onChange={(e) =>
          props.onChange({ ...props.value, hepsiburada_sku: e.target.value })
        }
        fullWidth
      />

      <NumberInput
        label='Stok Adedi'
        value={props.value.quantity}
        onChange={(newQty) =>
          props.onChange({ ...props.value, quantity: newQty })
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={trLocale}>
          <DatePicker
            label='Son Kullanma Tarihi'
            disabled={!props.value.expiryDate}
            onChange={(newExpiry) =>
              props.onChange({
                ...props.value,
                expiryDate: newExpiry || undefined,
              })
            }
            value={props.value.expiryDate ?? null}
            inputFormat='d MMMM yyyy'
            renderInput={(params) => (
              <TextField {...params} size='small' fullWidth />
            )}
          />
        </LocalizationProvider>
        <Switch
          checked={!!props.value.expiryDate}
          onChange={() =>
            props.onChange({
              ...props.value,
              expiryDate: props.value.expiryDate ? undefined : new Date(),
            })
          }
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
        <NumberInput
          label='Özel Fiyat'
          value={props.value.pricing.specialPrice}
          onChange={(newPrice) =>
            props.onChange({
              ...props.value,
              pricing: { ...props.value.pricing, specialPrice: newPrice },
            })
          }
          acceptFloat
          endAdornment={<Typography>₺</Typography>}
        />
        {[0, 1].map((i) => (
          <NumberInput
            key={i}
            label={`Fiyat ${i + 1}`}
            value={props.value.pricing.prices[i]}
            onChange={(newPrice) => handlePriceChange(i, newPrice)}
            acceptFloat
            endAdornment={<Typography>₺</Typography>}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
        {[2, 3, 4].map((i) => (
          <NumberInput
            key={i}
            label={`Fiyat ${i + 1}`}
            value={props.value.pricing.prices[i]}
            onChange={(newPrice) => handlePriceChange(i, newPrice)}
            acceptFloat
            endAdornment={<Typography>₺</Typography>}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={props.value.isActive}
              onChange={() =>
                props.onChange({
                  ...props.value,
                  isActive: !props.value.isActive,
                })
              }
            />
          }
          label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
        />

        <Button
          variant='contained'
          color='error'
          onClick={() => setClickedDelete(true)}
          startIcon={<DeleteIcon />}
          sx={{ ...sharedStyles.buttonText, height: 'fit-content' }}
        >
          Sil
        </Button>
      </Box>
    </Paper>
  );
};
